<template>
    <div class="w-full p-6">
        <h1 class="text-2xl font-bold mb-2">Forecasting for Planners</h1>
        <p class="text-muted-foreground mb-6">Create and manage forecasting data for operational planning and
            resource allocation</p>

        <!-- Your Template Section -->
        <ForecastingTemplateView
                class="mb-6"
                @quarterChanged="onQuarterChanged"
                :documentDates="quarterDocuments"
        />
        <div class="w-full flex gap-5">
            <div class="flex-grow bg-white rounded-lg shadow p-6 mb-6">
                <h3 class="text-xl font-semibold mb-4">Upload Report for Quarter {{ selectedQuarter }}</h3>

                <div class="mb-4">
                    <input
                            type="file"
                            id="excel-template"
                            accept=".xlsx, .xls, .csv"
                            @change="handleFileUpload"
                            class="hidden"
                            ref="fileInput"
                    />

                    <button
                            @click="triggerFileUpload"
                            class="bg-blue-700 text-white px-4 py-2 rounded-md flex items-center"
                            :disabled="isUploading"
                    >
                        <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                             xmlns="http://www.w3.org/2000/svg">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                        </svg>
                        Upload Excel File
                    </button>

                </div>

                <!-- Upload progress indicator -->
                <div v-if="isUploading"
                     class="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4 flex items-center">
                    <svg class="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none"
                         viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Processing file...</span>
                </div>

                <!-- Success message after upload -->
                <div v-if="uploadSuccess"
                     class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 flex items-center">
                    <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                         xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>{{ uploadSuccess }}</span>
                </div>

                <!-- Display filename after upload -->
                <div v-if="selectedFile"
                     class="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded mb-4">
                    <div class="flex items-center">
                        <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                             xmlns="http://www.w3.org/2000/svg">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                        </svg>
                        <span>{{ selectedFile.name }}</span>
                    </div>
                    <div v-if="documentDates && (documentDates.updated_at || documentDates.created_at)"
                         class="ml-7 mt-1 text-gray-500 text-sm">
                        <template v-if="documentDates.updated_at || documentDates.created_at">
                            {{ documentDates.updated_at ? 'Last updated' : 'Created' }}:
                            {{
                            formatDate(new Date(documentDates.updated_at || documentDates.created_at || new Date().toISOString()))
                            }}
                        </template>
                    </div>
                </div>

                <!-- Error message for problems -->
                <div v-if="uploadError"
                     class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
                    <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                         xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span>{{
                        uploadError instanceof Object && 'message' in uploadError ? uploadError.message : uploadError
                        }}</span>
                </div>

            </div>

            <div class="sticky-sidebar">
                <div class="h-full">
                    <ChatbotInterface
                            v-if="userData && userData?.id"
                            :userData="userData"
                            :environment="currentEnv"
                            :userId="userData?.id"
                            class="h-full chatbot-container"
                            style="min-width: 600px; max-width: 600px; position: sticky; top: 0;"/>
                </div>
            </div>
        </div>
        <!-- Upload Excel Template Section -->
    </div>
</template>

<script lang="ts">
import {defineComponent, onMounted, PropType, ref, Ref, watch} from 'vue';
import ForecastingTemplateView from './ForecastingTemplateView.vue';
import {ForecastingParserService} from '../features/forecasting/services/forecastingParser';
import {ForecastSchedule} from '../features/forecasting/models/types';
import {AppError, ErrorType, withErrorHandling} from '../utils/errorHandling';
import {ChatbotEnv} from '@/types/chatbotEnv';
import ChatbotInterface from './chatbot/ChatbotInterface.vue';

import { BusinessRole } from '@/types/businessRole';
import { UserData } from './Chatbot.vue';
import { ExcelTemplateUploadService } from '@/services/excelTemplateUploadService';
import { useAuth } from '@/composables/useAuth';
import { useTasks } from '@/composables/useTasks';
import { supabase } from '@/services/supabase';
import { logger, LogCategory } from '@/services/logger';
import {DOCUMENT_STATUS} from '@/types/document';
import { useDueDates } from '@/composables/useDueDates';
import { ACTIONS } from '@/types/taskActions';

export default defineComponent({
    name: 'ForecastingPlannersView',
    components: {
        ForecastingTemplateView,
        ChatbotInterface
    },
    props: {
        userData: {
            type: Object as PropType<UserData>,
            default: () => ({
                name: 'User',
                avatar: null,
                business_role: BusinessRole.PLANNER
            })
        }
    },
    setup(props) {
        // Refs for file upload
        const fileInput = ref<HTMLInputElement | null>(null);
        const selectedFile = ref<File | null>(null);
        const uploadError = ref<Error | AppError | null>(null);
        const uploadSuccess = ref<string | null>(null);
        const isUploading = ref<boolean>(false);

        // Ref for document dates
        const documentDates = ref<{ created_at?: string, updated_at?: string } | null>(null);

        // Ref for selected quarter
        const selectedQuarter = ref('');
        const selectedForecastId = ref('');

        // Ref for all documents in the selected quarter
        const quarterDocuments = ref<Array<{
            id: string,
            document_id: string,
            name: string,
            created_at: string,
            updated_at?: string,
            status?: string,
            dateDisplay?: {
                type: string,
                date: string
            }
        }>>([]);

        // Handler for quarter changes from ForecastingTemplateView
        const onQuarterChanged = async (quarter: string) => {

            if (quarter) {
                const {data} = await supabase
                    .from('forecasting_process')
                    .select('id, name')
                    .eq('name', quarter).single();

                selectedForecastId.value = data?.id || '';
            }

            selectedQuarter.value = quarter;

            // Fetch all documents for this quarter
            await fetchQuarterDocuments();
        };

        /**
         * Fetches all documents for the selected quarter with specific statuses
         * for the current user's report type
         */
        const fetchQuarterDocuments = async () => {
            if (!selectedQuarter.value) {
                logger.warn('Cannot fetch documents: Missing quarter information', LogCategory.GENERAL);
                return;
            }

            if (!user.value) {
                logger.warn('Cannot fetch documents: No user information available', LogCategory.GENERAL);
                return;
            }

            try {
                // First, get the user's report type
                const {data: userProfile, error: userError} = await supabase
                    .from('profiles')
                    .select('report_type')
                    .eq('id', user.value.id)
                    .single();

                if (userError) {
                    logger.error('Error fetching user profile', LogCategory.DATABASE, {
                        error: userError,
                        userId: user.value.id
                    });
                    return;
                }

                const userReportType = userProfile?.report_type;

                // Now fetch documents with the user's report type
                const {data: documents, error} = await supabase
                    .from('documents')
                    .select('id, name, created_at, updated_at, status, report_type, forecasting_process!inner(*)')
                    .eq('forecasting_process.name', selectedQuarter.value)
                    .eq('report_type', userReportType)
                    .in('status', [
                        DOCUMENT_STATUS.READY_FOR_DOWNLOAD,
                        DOCUMENT_STATUS.DOWNLOADED,
                        DOCUMENT_STATUS.UPLOADED_CHECKING,
                        DOCUMENT_STATUS.ADJUSTMENT_NEEDED
                    ])
                    .order('created_at', {ascending: false});

                if (error) {
                    logger.error('Error fetching documents for quarter', LogCategory.DATABASE, {
                        error,
                        quarter: selectedQuarter.value
                    });
                    return;
                }

                if (documents && documents.length > 0) {
                    // Format the documents to include formatted dates
                    quarterDocuments.value = documents.map(doc => ({
                        id: doc.id,
                        document_id: doc.id, // Add document_id to match what's used in ForecastingTemplateView
                        name: doc.name,
                        created_at: doc.created_at,
                        updated_at: doc.updated_at,
                        status: doc.status,
                        // Add formatted date string for display
                        dateDisplay: {
                            type: doc.updated_at ? 'Last updated' : 'Created',
                            date: formatDate(new Date(doc.updated_at || doc.created_at))
                        }
                    }));
                } else {
                    quarterDocuments.value = [];
                    logger.info(`No documents found for quarter ${selectedQuarter.value}`, LogCategory.GENERAL);
                }
            } catch (error) {
                logger.error('Exception fetching documents for quarter', LogCategory.DATABASE, {
                    error,
                    quarter: selectedQuarter.value
                });
            }
        };

        // Ref for parsed data
        const scheduleData = ref<ForecastSchedule | null>(null);

        // Ref for uploaded document ID
        const uploadedDocumentId = ref<string | null>(null);

        // Ref for controller user IDs
        const controllerUserIds = ref<string[]>([]);

        // Ref for confirmation dialog
        const showConfirmDialog = ref<boolean>(false);

        // Service for parsing Excel file
        const forecastingParser = new ForecastingParserService();

        // Service for uploading Excel templates
        const excelTemplateUploadService = new ExcelTemplateUploadService();

        // Auth composable
        const {checkAndRefreshAuth, user} = useAuth();

        // Tasks composable
        const {fetchTasks, uploadActionTasks, completeTask, createTask} = useTasks();


        // Configurable file size (null means no limit)
        const maxFileSize = ref<number | null>(10 * 1024 * 1024); // 10MB default limit

        /**
         * Formats file size into a readable form
         * @param bytes The file size in bytes
         * @returns Formatted file size (e.g., "10.00 MB")
         */
        const formatFileSize = (bytes: number | null): string => {
            if (bytes === null) return 'Unlimited';

            const units = ['B', 'KB', 'MB', 'GB'];
            let size = bytes;
            let unitIndex = 0;

            while (size >= 1024 && unitIndex < units.length - 1) {
                size /= 1024;
                unitIndex++;
            }

            return `${size.toFixed(2)} ${units[unitIndex]}`;
        };

        /**
         * Validates file size
         * @param file The file to validate
         * @returns true if the file meets the size constraint, otherwise false
         */
        const validateFileSize = (file: File): boolean => {
            // If no limit is set, always return true
            if (maxFileSize.value === null) return true;

            // Otherwise check if the file size is below the limit
            return file.size <= maxFileSize.value;
        };

        /**
         * Triggers the file upload dialog
         */
        const triggerFileUpload = () => {
            fileInput.value?.click();
        };

        /**
         * Handles file upload
         * @param event The event object
         */
        const handleFileUpload = async (event: Event) => {
            const input = event.target as HTMLInputElement;
            if (input.files && input.files.length > 0) {
                const file = input.files[0];

                // Validate file size
                if (!validateFileSize(file)) {
                    uploadError.value = {
                        type: ErrorType.VALIDATION,
                        message: `The file is too large. Maximum size: ${formatFileSize(maxFileSize.value)}`
                    };
                    return;
                }

                // Reset upload status
                uploadError.value = null;
                uploadSuccess.value = null;
                isUploading.value = true;
                scheduleData.value = null;

                await withErrorHandling(
                    async () => {
                        // Create a ref for the filename
                        const fileName = ref(file.name);

                        // Check and refresh authentication if needed
                        await checkAndRefreshAuth();

                        // Upload the file with UPLOADED_CHECKING status
                        await excelTemplateUploadService.uploadTemplateFile(
                            fileName,
                            ref(file),
                            selectedForecastId.value,
                            props.userData
                        );

                        // Save file and show success message
                        selectedFile.value = file;
                        uploadSuccess.value = `File "${file.name}" successfully uploaded and is being checked.`;

                        await fetchUploadTasksForQuarter();
                        await queryRecentlyCreatedDocument();
                        await queryControllerUsers();
                        await createReviewTasksForControllers();

                        await fetchQuarterDocuments();
                    },
                    uploadError as Ref<Error | AppError | null>,
                    ErrorType.STORAGE,
                    'Error uploading file'
                );
                // Reset upload status
                isUploading.value = false;
            }
        };

        /**
         * Shows the confirmation dialog
         */
        const showParseConfirmation = () => {
            showConfirmDialog.value = true;
        };

        /**
         * Parses the uploaded Excel file
         */
        const parseFile = async () => {
            if (!selectedFile.value) {
                uploadError.value = {
                    type: ErrorType.VALIDATION,
                    message: 'Please select a file first'
                };
                return;
            }

            // Reset upload status
            uploadError.value = null;
            isUploading.value = true;

            await withErrorHandling(
                async () => {
                    // Parse Excel file
                    const data = await forecastingParser.parseExcelFile(selectedFile.value!);

                    // Save data
                    scheduleData.value = data;

                    // Show success message
                    uploadSuccess.value = `File "${selectedFile.value!.name}" successfully processed.`;
                },
                uploadError as Ref<Error | AppError | null>,
                ErrorType.VALIDATION,
                'Error parsing Excel file'
            );

            // Reset upload status
            isUploading.value = false;
        };

        /**
         * Fetch tasks related to the selected quarter with UPLOAD action
         * to find out which task needs to be completed
         */
        const fetchUploadTasksForQuarter = async () => {
            console.log('Fetching upload tasks for quarter:', selectedQuarter.value);
            if (!selectedQuarter.value || !user.value) {
                logger.warn('Cannot fetch tasks: Missing quarter or user information', LogCategory.GENERAL);
                return;
            }

            // Fetch all tasks
            await fetchTasks();

            // Filter upload tasks by assigned user
            const userUploadTasks = uploadActionTasks.value.filter(task =>
                task.assigned_user_id === user.value?.id
            );

            // Further filter tasks by checking if the forecasting process name of the document assigned to the task
            // matches the selected quarter
            const forecastResponse = await supabase
                .from('documents')
                .select('*, forecasting_process!inner(*)')
                .in('id', userUploadTasks.map(task => task.document_id))
                .eq('forecasting_process.name', selectedQuarter.value);

            if (forecastResponse.error) {
                logger.error('Error fetching documents with forecasting process', LogCategory.DATABASE, {
                    error: forecastResponse.error,
                    quarter: selectedQuarter.value
                });
                return;
            }

            // Get the document IDs that match our criteria
            const matchingDocumentIds = forecastResponse.data.map(doc => doc.id);

            // Filter tasks to only those with matching document IDs
            const matchingTasks = userUploadTasks.filter(task =>
                matchingDocumentIds.includes(task.document_id)
            );

            console.log(`Found ${matchingTasks.length} upload tasks for quarter ${selectedQuarter.value}:`, matchingTasks);

            // Complete the matching tasks (usually should only be one task)
            if (matchingTasks.length > 0) {
                logger.info(`Completing ${matchingTasks.length} upload tasks`, LogCategory.GENERAL);

                for (const task of matchingTasks) {
                    try {
                        // Get the file name from the selected file
                        const fileName = selectedFile.value ? selectedFile.value.name : 'unknown';

                        // Complete the task with the description
                        let description = `Upload completed for document ${fileName}`;

                        await completeTask(task.id, description);

                        logger.info(`Task completed successfully`, LogCategory.GENERAL, {
                            taskId: task.id,
                            description
                        });
                    } catch (error) {
                        logger.error(`Failed to complete task`, LogCategory.GENERAL, {
                            taskId: task.id,
                            error
                        });
                    }
                }
            }

            return matchingTasks;
        };

        /**
         * Query for recently created document with the same name as the uploaded file
         * (the easiest way to find the document ID of the uploaded file)
         */
        const queryRecentlyCreatedDocument = async () => {
            if (!selectedFile.value) {
                logger.warn('Cannot query for document: No file selected', LogCategory.DATABASE);
                return;
            }

            try {
                // Calculate timestamp for 1 minute ago
                const oneMinuteAgo = new Date();
                oneMinuteAgo.setMinutes(oneMinuteAgo.getMinutes() - 1);
                const oneMinuteAgoIso = oneMinuteAgo.toISOString();

                const {data: recentDocuments, error: docError} = await supabase
                    .from('documents')
                    .select('id, name, created_at, updated_at')
                    .eq('name', selectedFile.value.name)
                    .gte('created_at', oneMinuteAgoIso)
                    .order('created_at', {ascending: false});

                if (docError) {
                    logger.error('Error querying for recent document', LogCategory.DATABASE, {
                        error: docError
                    });
                } else if (recentDocuments && recentDocuments.length > 0) {
                    // Use the most recently created document
                    const mostRecentDoc = recentDocuments[0];
                    uploadedDocumentId.value = mostRecentDoc.id;

                    // Store document dates for display
                    documentDates.value = {
                        created_at: mostRecentDoc.created_at,
                        updated_at: mostRecentDoc.updated_at
                    };

                    // Add to upload success message with the last updated date or created date
                    if (uploadSuccess.value) {
                        const dateToShow = documentDates.value.updated_at || documentDates.value.created_at;
                        if (dateToShow) {
                            const dateType = documentDates.value.updated_at ? 'Last updated' : 'Created';
                            uploadSuccess.value += ` ${dateType}: ${formatDate(new Date(dateToShow))}.`;
                        }
                    }
                } else {
                    logger.warn('No recently created document found', LogCategory.DATABASE, {
                        fileName: selectedFile.value.name,
                        createdAfter: oneMinuteAgoIso
                    });
                }
            } catch (error) {
                logger.error('Exception querying for recent document', LogCategory.DATABASE, {
                    error
                });
            }
        };

        /**
         * Query for users with CONTROLLER business role
         * (for review tasks should be created for users with this business role)
         */
        const queryControllerUsers = async () => {
            try {
                const {data: controllerUsers, error: profileError} = await supabase
                    .from('profiles')
                    .select('id, name, business_role')
                    .eq('business_role', BusinessRole.CONTROLLER);

                if (profileError) {
                    logger.error('Error querying for controller profiles', LogCategory.DATABASE, {
                        error: profileError
                    });
                } else if (controllerUsers && controllerUsers.length > 0) {
                    // Store the controller user IDs
                    controllerUserIds.value = controllerUsers.map(user => user.id);
                    console.log('Controller users found:', controllerUserIds.value);
                } else {
                    logger.warn('No controller users found', LogCategory.DATABASE);
                }
            } catch (error) {
                logger.error('Exception querying for controller profiles', LogCategory.DATABASE, {
                    error
                });
            }
        };

        /**
         * Create review tasks for controller users found in the previous step
         */
        const createReviewTasksForControllers = async () => {
            if (!uploadedDocumentId.value || controllerUserIds.value.length === 0) {
                logger.warn('Cannot create review tasks: Missing document ID or controller users', LogCategory.GENERAL);
                return;
            }

            try {
        // Calculate due date using the getDueDate function
        const { getDueDate } = useDueDates();
        const dueDateString = await getDueDate(ACTIONS.REVIEW, uploadedDocumentId.value);

                // Create a task for each controller user
                for (const userId of controllerUserIds.value) {
                    try {
                        await createTask({
                            action: 'REVIEW',

                            due_at: dueDateString,
                            assigned_user_id: userId,
                            document_id: uploadedDocumentId.value,
                            description: `Review the uploaded document "${selectedFile.value?.name}"`
                        });
                    } catch (error) {
                        logger.error('Failed to create review task', LogCategory.DATABASE, {
                            userId,
                            documentId: uploadedDocumentId.value,
                            error
                        });
                    }
                }
            } catch (error) {
                logger.error('Exception creating review tasks', LogCategory.DATABASE, {
                    error
                });
            }
        };

        /**
         * Formats a date into a readable form
         * @param date The date to format
         * @returns Formatted date (e.g., "April 15, 2025, 2:30 PM")
         */
        const formatDate = (date: Date): string => {
            return date.toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        };

        const currentEnv = ChatbotEnv.PLANNER_FILE_HANDLING;

        // Watch for user changes and fetch documents when user becomes available
        watch(user, async (newUser) => {
            if (newUser && selectedQuarter.value) {
                logger.info('User is now available, fetching documents', LogCategory.GENERAL, {
                    userId: newUser.id
                });
                await fetchQuarterDocuments();
            }
        });

        // Fetch documents when component is mounted
        onMounted(async () => {
            if (selectedQuarter.value && user.value) {
                await fetchQuarterDocuments();
            } else if (selectedQuarter.value) {
                logger.info('Component mounted but user not available yet, will fetch documents when user is available', LogCategory.GENERAL);
            }
        });

        return {
            fileInput,
            selectedFile,
            uploadError,
            uploadSuccess,
            isUploading,
            scheduleData,
            showConfirmDialog,
            uploadedDocumentId,
            controllerUserIds,
            documentDates,
            quarterDocuments,
            triggerFileUpload,
            handleFileUpload,
            showParseConfirmation,
            parseFile,
            formatDate,
            currentEnv,
            selectedQuarter,
            onQuarterChanged,
            fetchUploadTasksForQuarter,
            fetchQuarterDocuments,
            queryControllerUsers // <-- Expose this function to the template
        };
    }
});
</script>

<style scoped>
/* Custom width classes */
.w-6\/10 {
    width: 60%;
}

.w-4\/10 {
    width: 40%;
}

/* Sticky sidebar for the chatbot */
.sticky-sidebar {
    position: sticky;
    top: 1rem;
    height: calc(100vh - 4rem);
    max-height: 800px;
    overflow-y: hidden;
}

/* Chatbot container with scrolling */
.chatbot-container {
    height: 100%;
    overflow-y: auto;
    border-radius: 0.5rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* Media query for smaller screens */
@media (max-width: 768px) {
    .flex-row {
        flex-direction: column;
    }

    .w-6\/10,
    .w-4\/10 {
        width: 100%;
    }

    .sticky-sidebar {
        position: relative;
        height: 500px;
        margin-top: 1rem;
    }
}
</style>
